import PageLoader from '@components/PageLoader'
import { useAuth } from '@context/AuthContext'
import { useMe } from '@hooks/useMe'
import React from 'react'
import { Navigate } from 'react-router-dom'
import AdminLayout from './AdminLayout'

/**
 * Gates the `/admin/*` tree on the current user's role.
 *
 * Authentication is checked first (redirect to login if no token), then the role
 * is read from `/me`. Non-admins are bounced to `/home` so the admin surface is
 * completely inaccessible to them. This is UI gating only — the backend AdminGuard
 * independently enforces every admin request.
 */
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { token, isLoading: isAuthLoading } = useAuth()
	const { data: user, isLoading: isUserLoading, isError } = useMe()

	if (isAuthLoading) {
		return <PageLoader label="Loading…" />
	}

	if (!token) {
		return <Navigate to="/" replace />
	}

	if (isUserLoading) {
		return <PageLoader label="Loading…" />
	}

	if (isError || user?.role !== 'ADMIN') {
		return <Navigate to="/home" replace />
	}

	return <AdminLayout>{children}</AdminLayout>
}

export default AdminRoute
